// +----------------------------------------------------------------------
// | useQuota：拉「今天还能用几次」并维护它（帮我想想 / 聊天共用一套）
// |
// | 为什么单独抽出来：
// |   首页要显示「今天还能问 3 次」、超限时要立刻把它刷成 0、会员要显示
// |   「不限次」—— 这三个动作分散在页面里会各写一遍。收在这里，
// |   页面只读 quota / reload / applyFrom。
// |
// | 数据源：GET /api/membership 的 decision_quota / quota 字段。
// | 不复用 useUser 的 stats，因为额度是「会随动作变的即时状态」，
// | 而 stats 是「累计统计」——混在一起会让缓存刷新时机很难理清。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { getMembership } from '../api/order';
import useAuth from './useAuth';

/**
 * @param {'decision'|'chat'} kind 要哪种额度
 *   decision → 「帮我想想」每天几次（membership.decision_quota）
 *   chat     → 聊天每天几条（membership.quota）
 */
export default function useQuota(kind = 'decision') {
  const { isLoggedIn } = useAuth();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);

  const key = kind === 'chat' ? 'quota' : 'decision_quota';

  const reload = useCallback(async () => {
    if (!isLoggedIn) {
      setQuota(null);
      return null;
    }
    setLoading(true);
    try {
      const data = await getMembership();
      const next = (data && data[key]) || null;
      setQuota(next);
      return next;
    } catch (e) {
      // 拉不到就不显示额度，别让一个次要信息把页面搞出错误态
      return null;
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, key]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * 直接把服务端刚刚返回的额度铺上去
   *
   * 用在两处：
   *   - 超限报错时，err.data.quota 就是服务端算好的最新状态（比再请求一次准）
   *   - 分析成功后，把那次的 quota 带回来，计数器立刻减 1，不用等下次进页面
   */
  const applyFrom = useCallback((next) => {
    if (next && typeof next === 'object') setQuota(next);
  }, []);

  return { quota, loading, reload, applyFrom };
}
